import type { ProductListResult } from '@partful/shared-types';
import { CADUploadJob, Job } from '@providers/job/types';

export interface JobWithRedirectURL {
  job: Job;
  redirectURL: string;
}

interface FilteredJobs {
  processingJobs: JobWithRedirectURL[];
  failedProcessingJobs: JobWithRedirectURL[];
  inProgressJobs: JobWithRedirectURL[];
}

export function getJobsWithRedirectURLs(jobs: Job[]): JobWithRedirectURL[] {
  const jobsWithRedirectURLS: JobWithRedirectURL[] = [];

  // needed to distinguish sBOM createProduct/manageProduct redirectURLs & cadJobs to hide (matching sBOMJob)
  const cadJobMap = jobs.reduce((acc: { [key: string]: string }, job) => {
    if (job.jobType === 'CAD' && job.status !== 'COMPLETED') {
      acc[job.productName] = job.runID;
    }
    return acc;
  }, {});

  const createProductSBOMJobNames = jobs
    .filter((job) => job.jobType === 'SalesBOM' && cadJobMap[job.productName])
    .map((job) => job.productName);

  jobs.forEach((job) => {
    const cadJobWithNoSBOMJob = job.jobType === 'CAD' && !createProductSBOMJobNames.includes(job.productName);
    if (cadJobWithNoSBOMJob) {
      jobsWithRedirectURLS.push({
        job,
        redirectURL: cadURLProgressMap(job),
      });
    }

    if (job.jobType === 'SalesBOM') {
      const cadJobRunID = cadJobMap[job.productName];

      if (cadJobRunID) {
        jobsWithRedirectURLS.push({
          job,
          redirectURL: `/create-product/sales-bom?configurationPID=${job.configurationPID}&productName=${job.productName}&runID=${cadJobRunID}`,
        });
      } else {
        jobsWithRedirectURLS.push({
          job,
          redirectURL: `/manage-product/${job.productName}/sales-bom?configurationPID=${job.configurationPID}`,
        });
      }
    }

    if (job.jobType === 'PartCategories') {
      jobsWithRedirectURLS.push({
        job,
        redirectURL: `/manage-product/${job.productName}/parts-categories?configurationPID=${job.configurationPID}&runID=${job.runID}`,
      });
    }
  });

  return jobsWithRedirectURLS;
}

export function filterJobsByProcessingState(jobsWithURLs: JobWithRedirectURL[]): FilteredJobs {
  const filteredJobs: FilteredJobs = {
    processingJobs: [],
    failedProcessingJobs: [],
    inProgressJobs: [],
  };

  jobsWithURLs.forEach((jobWithURL) => {
    if (isProcessing(jobWithURL.job)) {
      filteredJobs.processingJobs.push(jobWithURL);
    }

    if (isProcessingFailed(jobWithURL.job)) {
      filteredJobs.failedProcessingJobs.push(jobWithURL);
    }

    if (isJobInProgress(jobWithURL.job)) {
      filteredJobs.inProgressJobs.push(jobWithURL);
    }
  });

  return filteredJobs;
}

const isProcessing = (job: Job): boolean => job.status === 'IN_PROGRESS_PROCESSING_CAD';

const isProcessingFailed = (job: Job): boolean => job.status.endsWith('FAILED');

export const isJobInProgress = (job: Job): boolean => {
  if (job.jobType === 'CAD') {
    return job.status.startsWith('IN_PROGRESS_') && !job.status.includes('PROCESSING');
  }

  return job.status.startsWith('IN_PROGRESS');
};

export const cadURLProgressMap = (job: CADUploadJob): string => {
  // TODO: this can be removed once https://samsonvt.atlassian.net/browse/MER-454 is done
  switch (job.status) {
    case 'IN_PROGRESS_REQUIRES_CAD_FILE':
      // No need to redirect with the product name for this one as the user could change the name
      return `/create-product/cad?runID=${job.runID}`;

    case 'IN_PROGRESS_REQUIRES_GENERAL_ASSEMBLY':
      // ConfigurationPID is only set after CAD processing is complete
      return `/create-product/cad/general-assembly?runID=${job.runID}&productName=${job.productName}`;

    case 'IN_PROGRESS_PROCESSING_CAD':
    case 'IN_PROGRESS_CAD_PROCESSING_FAILED':
    case 'FAILED':
      // Failed state should be filtered out by API
      return `/create-product/cad/processing?runID=${job.runID}&productName=${job.productName}`;

    case 'IN_PROGRESS_REQUIRES_ATTRIBUTE_MAPPING':
      return `/create-product/cad/select-part-number?runID=${job.runID}&configurationPID=${job.configurationPID}&productName=${job.productName}`;

    case 'IN_PROGRESS_REQUIRES_SALES_BOM':
      return `/create-product/sales-bom?configurationPID=${job.configurationPID}&productName=${job.productName}&runID=${job.runID}`;

    /* istanbul ignore next */
    default:
      return '/product-library?error=CouldntMapProduct';
  }
};

export function filterUncategorizedProducts(products: ProductListResult[]): ProductListResult[] {
  return products.filter((product) => !product.productCategories || product.productCategories.length === 0);
}
